import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, CalendarClock, CheckCircle2, Clock, HandCoins, Percent, Send } from 'lucide-react';
import { aud } from './utils';

const fade = (i) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, delay: 0.05 * i },
});

const Kpi = ({ i, icon: Icon, label, value, sub, testId, tone = 'text-[#3A3530]', accent = 'bg-[#8B9D83]/10 text-[#6B7D63]', glow }) => (
  <motion.div {...fade(i)}
    className={`rounded-2xl border bg-white p-5 transition-shadow duration-200 hover:shadow-md ${glow ? 'pp-glow-red border-[#C0392B]/40' : 'border-[#E8E4DC]'}`}
    data-testid={testId}>
    <div className="flex items-start justify-between">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#9B9590]">{label}</p>
      <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${accent}`}><Icon className="h-4 w-4" /></span>
    </div>
    <p className={`mt-3 text-2xl font-bold tabular-nums ${tone}`} data-testid={`${testId}-value`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-[#9B9590]">{sub}</p>}
  </motion.div>
);

const Hero = ({ k, periodLabel }) => (
  <motion.div {...fade(0)} className="relative overflow-hidden rounded-2xl bg-[#2F3A32] p-6 text-white lg:col-span-2 lg:row-span-2" data-testid="kpi-total-owing">
    <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#8B9D83]/25 blur-3xl" />
    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#C9D6C1]">Total still owing · active plans</p>
    <p className="mt-3 font-heading text-4xl font-semibold tabular-nums sm:text-5xl" data-testid="kpi-total-owing-value">{aud(k.total_owing)}</p>
    <p className="mt-2 text-sm text-[#C9D6C1]">From Halaxy balances across {k.active_count} active plan{k.active_count === 1 ? '' : 's'}</p>
    <div className="mt-6 grid grid-cols-2 gap-4 border-t border-white/10 pt-5">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-[#C9D6C1]">Original balances</p>
        <p className="mt-1 text-xl font-semibold tabular-nums" data-testid="kpi-total-original">{aud(k.total_original)}</p>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-[#C9D6C1]">Collected · {periodLabel}</p>
        <p className="mt-1 text-xl font-semibold tabular-nums text-[#B8D89A]" data-testid="kpi-collected-hero">{aud(k.collected_period)}</p>
      </div>
    </div>
  </motion.div>
);

export const KpiCards = ({ kpis: k, periodLabel }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="kpi-grid">
    <Hero k={k} periodLabel={periodLabel} />
    <Kpi i={1} icon={AlertTriangle} label="Needs follow-up" value={k.needs_follow_up} testId="kpi-needs-follow-up"
      sub={`${k.failed_count} failed payment${k.failed_count === 1 ? '' : 's'}`} glow={k.needs_follow_up > 0}
      tone={k.needs_follow_up > 0 ? 'text-[#C0392B]' : 'text-[#3A3530]'} accent="bg-[#C0392B]/10 text-[#C0392B]" />
    <Kpi i={2} icon={CalendarClock} label="Expected next 7 days" value={aud(k.expected_next_7)} testId="kpi-expected-7"
      sub={`${k.payment_due} payment${k.payment_due === 1 ? '' : 's'} due in 2 days`} accent="bg-[#D4A574]/15 text-[#8B6B4A]" />
    <Kpi i={3} icon={Percent} label="Collection rate" testId="kpi-collection-rate"
      value={k.collection_rate === null ? '—' : `${k.collection_rate}%`}
      sub={`${aud(k.collected_period)} of ${aud(k.expected_period)} expected`}
      tone={k.collection_rate !== null && k.collection_rate < 80 ? 'text-[#C17B6B]' : 'text-[#4A6B2E]'} />
    <Kpi i={4} icon={HandCoins} label="Active plans" value={k.active_count} testId="kpi-active"
      sub={`${k.escalated} escalated`} accent="bg-[#6B8FA3]/15 text-[#3A5A6B]" />
    <Kpi i={5} icon={Send} label="Follow-up sent" value={k.follow_up_sent} testId="kpi-follow-up-sent"
      sub="Awaiting client payment" accent="bg-[#6B8FA3]/15 text-[#3A5A6B]" />
    <Kpi i={6} icon={Clock} label={`Collected · ${periodLabel}`} value={aud(k.collected_period)} testId="kpi-collected"
      tone="text-[#4A6B2E]" sub="Plan payments received in Halaxy" />
    <Kpi i={7} icon={CheckCircle2} label="Completed" value={k.completed_count} testId="kpi-completed"
      sub="Paid in full ($0 balance)" accent="bg-[#7BA05B]/15 text-[#4A6B2E]" />
  </div>
);
